import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { hasStoredSupabaseSession, ESPERA_MAX_SESION_MS, ESPERA_MAX_PERFIL_MS, ESPERA_MAX_ARRANQUE_MS } from '@/lib/storedSessionHint';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { loginWithUsername } from '@/lib/usernameLogin';
import { resetCities } from "@/lib/savedCities";
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n/config';
import { isDespiaNative } from '@/lib/isDespiaNative';
import { alTropezarLaRed, fijarUsuarioOffline, leerEstadoOffline, modoActivo, sinRedSegura } from '@/lib/offline/estadoOffline';
import { sesionGuardada } from '@/lib/offline/sesionGuardada';
import { borrarDatosOffline, prepararDatosOffline, ESPERA_SESION_GUARDADA_MS } from '@/lib/offline/sesionOffline';

interface Profile {
  id: string;
  user_id: string;
  company_id: string | null;
  full_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_super_admin: boolean | null;
  has_seen_welcome: boolean | null;
  has_completed_onboarding_intent: boolean | null;
  has_completed_app_onboarding: boolean | null;
  username?: string | null;
}

interface Company {
  id: string;
  name: string;
  logo_url: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  subscription_expires_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  max_users: number | null;
  max_locations: number | null;
  max_greenhouses: number | null;
}

type AppRole = 'admin' | 'manager' | 'worker' | 'perito';

interface UserRole {
  role: AppRole;
  company_id: string;
}

interface AccessCheckResult {
  allowed: boolean;
  reason: string | null;
}

interface LinkedCompany {
  company_id: string;
  company_name: string;
  company_logo: string | null;
  /** false = esa empresa no tiene la suscripción activa (M40). Sin el dato, se da por buena. */
  is_usable?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  company: Company | null;
  role: AppRole | null;
  loading: boolean;
  subscriptionExpired: boolean;
  linkedCompanies: LinkedCompany[];
  /** True only for the very first admin of the company (earliest created admin profile). */
  isFirstAdmin: boolean;
  /**
   * Perito desvinculado de su única empresa (P3, 26-09-2026): la cuenta existe, sin
   * empresa ni rol. El enrutador le enseña «No estás vinculado a ninguna empresa».
   */
  peritoSinEmpresa: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, companyName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [linkedCompanies, setLinkedCompanies] = useState<LinkedCompany[]>([]);
  const [isFirstAdmin, setIsFirstAdmin] = useState(false);
  const [peritoSinEmpresa, setPeritoSinEmpresa] = useState(false);
  /* La espera de arranque cuando hay sesión en el disco pero todavía no en la
     mano. Ver `storedSessionHint`. */
  const esperaSesionRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    /* EL SUELO DEL ARRANQUE, ARMADO ANTES DE PEDIRLE NADA A LA BIBLIOTECA.
       Los otros tres relojes de esta pantalla se arman DENTRO del `.then` de
       `getSession()`, así que ninguno existe si esa promesa no contesta. Éste sí:
       vive fuera, y es lo único que separa «la app tarda» de «la app no abre
       nunca». Ver `ESPERA_MAX_ARRANQUE_MS`. */
    let relojArranque: number | null = window.setTimeout(() => {
      relojArranque = null;
      setLoading(false);
    }, ESPERA_MAX_ARRANQUE_MS);
    const soltarArranque = () => {
      if (relojArranque !== null) {
        window.clearTimeout(relojArranque);
        relojArranque = null;
      }
    };

    /* ENTRAR CON LA SESIÓN GUARDADA CUANDO NO HAY RED (modo sin conexión).
       Sin cobertura y con el token caducado, `getSession()` tarda ~50 s en
       contestar (renueva con reintentos) y contesta «nula». Antes eso era
       pantalla en blanco 15 s y luego la de entrar. Ahora: sin red segura se
       entra YA con el usuario de la sesión guardada; con red dudosa, si la
       biblioteca no ha dado usuario en `ESPERA_SESION_GUARDADA_MS`, también. El
       perfil, el rol y la empresa salen entonces de la copia del teléfono (la
       red sin conexión contesta esas lecturas), y si la renovación llega luego,
       es el mismo usuario y no cambia nada. */
    // Solo con el interruptor de la empresa encendido (lo último que se supo en este aparato).
    const guardada = modoActivo() ? sesionGuardada() : null;
    const entrarConSesionGuardada = () => {
      if (!guardada || currentUserIdRef.current) return;
      const id = guardada.user.id;
      currentUserIdRef.current = id;
      fijarUsuarioOffline(id);
      soltarArranque();
      if (esperaSesionRef.current !== null) {
        window.clearTimeout(esperaSesionRef.current);
        esperaSesionRef.current = null;
      }
      setUser(guardada.user as unknown as User);
      setSession(guardada as unknown as Session);
      setLoading(true);
      fetchUserData(id);
    };
    let relojGuardada: number | null = null;
    let soltarTropiezo: (() => void) | null = null;
    if (guardada) {
      if (sinRedSegura()) {
        window.setTimeout(entrarConSesionGuardada, 0);
      } else {
        relojGuardada = window.setTimeout(() => {
          relojGuardada = null;
          entrarConSesionGuardada();
        }, ESPERA_SESION_GUARDADA_MS);
        // Supabase no contesta (p. ej. la renovación del token falla por red): se entra ya.
        soltarTropiezo = alTropezarLaRed(entrarConSesionGuardada);
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const newUserId = session?.user?.id ?? null;
        const prevUserId = currentUserIdRef.current;

        /* SIN RED, «NO HAY SESIÓN» NO ES «HA CERRADO SESIÓN» (modo sin conexión).
           Con el token caducado y sin cobertura la biblioteca acaba contestando
           una sesión nula (INITIAL_SESSION) pero NO borra la guardada: es un fallo
           de red, no una salida. Si ya se entró con la sesión guardada, se sigue
           dentro con lo del teléfono; cuando vuelva la red, la renovación entra
           sola con el mismo usuario. Una salida de verdad (SIGNED_OUT, o la sesión
           borrada del disco) sí saca. */
        if (modoActivo() && !newUserId && prevUserId && event !== 'SIGNED_OUT' && hasStoredSupabaseSession()) {
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Only re-hydrate profile/company when the user identity actually
        // changes (sign-in, sign-out, account switch). Silent events like
        // TOKEN_REFRESHED / USER_UPDATED for the same user must NOT flip
        // `loading` back to true — doing so unmounts the entire app tree
        // (RoleBasedRouter -> LoadingScreen) and wipes any open form/dialog
        // state when the tab regains focus.
        if (newUserId && newUserId !== prevUserId) {
          currentUserIdRef.current = newUserId;
          fijarUsuarioOffline(newUserId);
          prepararDatosOffline(newUserId);
          /* Ya está aquí: las dos esperas de arranque sobran, y dejarlas vivas
             apagarían `loading` en mitad de la hidratación del perfil. */
          soltarArranque();
          if (esperaSesionRef.current !== null) {
            window.clearTimeout(esperaSesionRef.current);
            esperaSesionRef.current = null;
          }
          setLoading(true);
          setTimeout(() => {
            fetchUserData(newUserId);
          }, 0);
        } else if (!newUserId && prevUserId) {
          currentUserIdRef.current = null;
          /* Salir es salir: lo guardado en el teléfono se va con la sesión. */
          void borrarDatosOffline(prevUserId, queryClient);
          setProfile(null);
          setCompany(null);
          setRole(null);
          setSubscriptionExpired(false);
          setIsFirstAdmin(false);
        }
        // Same-user events (TOKEN_REFRESHED, USER_UPDATED, etc.): keep
        // session/user fresh but otherwise do nothing — the UI stays mounted.
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      soltarArranque();
      /* Dentro con la sesión guardada y la biblioteca contesta «nula» por falta de
         red: NO se pisa el usuario (eso devolvía a la pantalla de entrar). */
      if (modoActivo() && !session?.user && currentUserIdRef.current && hasStoredSupabaseSession()) return;
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        if (currentUserIdRef.current === session.user.id) return; // ya se entró con la sesión guardada
        currentUserIdRef.current = session.user.id;
        fijarUsuarioOffline(session.user.id);
        prepararDatosOffline(session.user.id);
        // Same guard as above: keep loading=true while we hydrate the profile.
        setLoading(true);
        fetchUserData(session.user.id);
      } else if (hasStoredSupabaseSession()) {
        /* NO HAY USUARIO, PERO SÍ SESIÓN GUARDADA — y son cosas distintas.
           Soltar `loading` aquí es lo que pintaba la pantalla de entrar durante
           un parpadeo al abrir la app: la sesión estaba en el disco y la
           biblioteca aún no la había podido renovar. Se espera a que llegue por
           `onAuthStateChange`, con tope. Ver `storedSessionHint`. */
        esperaSesionRef.current = window.setTimeout(() => {
          esperaSesionRef.current = null;
          setLoading(false);
        }, ESPERA_MAX_SESION_MS);
      } else {
        setLoading(false);
      }
    }).catch((e) => {
      if (currentUserIdRef.current) return; // dentro con la sesión guardada
      /* SIN ESTO, UN RECHAZO DEJA LA APP MUDA. `getSession()` puede rechazar
         (el almacenamiento lanza, la renovación revienta de forma no prevista) y
         hasta hoy nadie lo recogía: `loading` se quedaba en `true` y la pantalla
         en blanco, sin error y sin nada que tocar. */
      soltarArranque();
      console.error('[auth] getSession falló al arrancar', e);
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      soltarArranque();
      if (relojGuardada !== null) window.clearTimeout(relojGuardada);
      soltarTropiezo?.();
      if (esperaSesionRef.current !== null) window.clearTimeout(esperaSesionRef.current);
    };
  }, []);


  /** P3: la base dice si esta cuenta es un perito sin empresa. Fallo = no. */
  const consultarPeritoSinEmpresa = async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('perito_sin_empresa' as any);
      if (error) return false;
      return data === true;
    } catch {
      return false;
    }
  };

  const fetchUserData = async (userId: string) => {
    /* EL SEGUNDO CAMINO A LA PANTALLA EN BLANCO (08-09-2026).
       El del 06-09 estaba en `AppAuth` y ya lleva su reloj. Éste está aquí: con
       sesión, quien llama a esto acaba de hacer `setLoading(true)` y este
       `await` no tiene tope. El `finally` de abajo cubre que la consulta
       CONTESTE o que FALLE — lo que no cubre es que no vuelva NUNCA, que es lo
       que hace un `fetch` en una red que se cae a medias: ni resuelve ni
       rechaza, se queda colgado. Y con `loading` en `true` para siempre, el
       primer guardián de `AppAuth` (`if (loading || …) return null`) deja la app
       en blanco sin remedio.

       Medido contra producción colgando esa consulta a propósito: a los 18 s
       seguía en blanco. Con esto, a los 12 se suelta.

       LO QUE PASA AL SOLTAR, dicho claro: se sigue sin perfil, así que `AppAuth`
       puede acabar cerrando la sesión y enseñando la pantalla de entrar. Es
       feo, pero se puede usar: se vuelve a entrar y se sigue trabajando. Una
       pantalla en blanco no deja hacer nada. Por eso 12 s y no 3: que salte solo
       cuando la consulta esté de verdad colgada, no cuando vaya lenta. */
    let reloj: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      reloj = null;
      setLoading(false);
    }, ESPERA_MAX_PERFIL_MS);
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError) throw profileError;
      
      // If no profile found (e.g. Google OAuth user who never registered)
      /* SIN RED ÚTIL, «NO HAY PERFIL» NO SE CREE (modo sin conexión). La respuesta
         pudo salir de la copia del teléfono, y cerrar la sesión por eso dejaría
         fuera —y sin sus datos guardados— a quien solo está sin cobertura. Se
         trata como un fallo de carga: la app reintenta cuando vuelva la red. */
      if (!profileData && modoActivo() && (sinRedSegura() || leerEstadoOffline().mostrandoGuardado)) {
        throw new Error('PROFILE_UNAVAILABLE_OFFLINE');
      }

      if (!profileData) {
        const currentPath = window.location.pathname;
        const searchParams = new URLSearchParams(window.location.search);
        const isCheckoutWelcome = currentPath === '/welcome' && searchParams.get('from') === 'checkout';
        const isRegisterFlow = currentPath === '/register';
        const { data: currentAuth } = await supabase.auth.getSession();
        const sessionUser = currentAuth.session?.user;
        const provider = sessionUser?.app_metadata?.provider;
        const isOAuth = provider === 'google' || provider === 'apple' ||
          sessionUser?.identities?.some((i) => i.provider === 'google' || i.provider === 'apple');
        // Native-app onboarding routes: keep the OAuth session alive so
        // /app-auth/free-signup can pre-fill the form with the Google/Apple
        // profile and call create-native-free-account to attach a company.
        // Without this guard we silently sign the user out the moment they
        // return from Google, which looks like "Google sign-in does nothing".
        const isNativeOnboarding =
          /* El enlace de invitación del perito (tanda 5): una cuenta sin empresa
             entra ahí para aceptar y quedarse como perito; si se le cerrara la
             sesión aquí, no podría aceptar nunca. */
          currentPath.startsWith('/unirse/') ||
          currentPath === '/app-auth' ||
          currentPath.startsWith('/app-auth/') ||
          currentPath === '/auth-bridge' ||
          currentPath.startsWith('/onboarding') ||
          currentPath === '/welcome';


        if (isDespiaNative() && isOAuth) {
          console.log('[oauth-native]', {
            stage: 'profileless-oauth-kept',
            path: currentPath,
            destination: '/app-auth/free-signup',
          });
          setProfile(null);
          setCompany(null);
          setRole(null);
          setLoading(false);
          if (!isNativeOnboarding) {
            navigate('/app-auth/free-signup', { replace: true });
          }
          return;
        }

        // During checkout/register/native-onboarding flow, don't sign out — let the page handle reconciliation
        if (isCheckoutWelcome || isRegisterFlow || isNativeOnboarding) {
          setProfile(null);
          setCompany(null);
          setRole(null);
          setLoading(false);
          return;
        }

        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        setProfile(null);
        setCompany(null);
        setRole(null);
        setSubscriptionExpired(false);
        setLoading(false);
        throw new Error('NO_PROFILE_FOUND');
      }
      
      setProfile(profileData);
      /* ¿Perito que se quedó sin empresa? (P3). Solo se pregunta si no hay empresa en
         el perfil o no hay rol en ella; cualquier fallo (migración sin aplicar, red)
         = no, y se queda como antes («No se ha podido cargar tu cuenta»). */
      let sinRolAqui = !profileData?.company_id;

      // Apply user's saved profile language so the UI renders in their preferred language
      const savedLang = (profileData as { language?: string | null })?.language;
      if (savedLang && i18n.language !== savedLang) {
        try {
          await i18n.changeLanguage(savedLang);
        } catch (e) {
          console.warn("[Auth] Could not apply saved language:", e);
        }
      } else if (!savedLang) {
        // Primera vez: el perfil no tiene idioma, así que se guarda el que trae el
        // dispositivo. Es lo que decide en qué idioma le llegan los avisos, y hasta
        // ahora solo se escribía si la persona lo cambiaba a mano en los ajustes —
        // por eso 49 de 69 perfiles estaban vacíos y recibían inglés.
        //
        // `saveUserLanguage` ignora los idiomas que la app no tiene (un teléfono en
        // francés, por ejemplo): en ese caso el perfil se queda vacío a propósito y el
        // envío resuelve el idioma por la empresa.
        try {
          const { saveUserLanguage } = await import("@/lib/userLanguage");
          await saveUserLanguage(i18n.language);
        } catch (e) {
          console.warn("[Auth] Could not persist device language:", e);
        }
      }

      // Fetch user role
      if (profileData?.company_id) {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('company_id', profileData.company_id)
          .single();

        if (roleError) {
          console.error('Role fetch error:', roleError);
          sinRolAqui = true;
        } else {
          setRole(roleData?.role ?? null);
          if (!roleData?.role) sinRolAqui = true;

          // If perito, fetch linked companies
          /* SE ESPERA (tanda 5, M40): el router decide con esta lista si un perito
             cuya empresa está sin suscripción puede pasar a otra. Sin esperarla,
             la primera pintura veía la lista vacía y le encerraba en la pantalla
             de pago de un cliente. */
          if (roleData?.role === 'perito') {
            await fetchLinkedCompanies(userId);
          } else {
            setLinkedCompanies([]);
          }

          // Resolve "first admin" flag (only meaningful for admin role).
          if (roleData?.role === 'admin') {
            try {
              const { data: firstAdminData, error: firstAdminError } = await supabase
                .rpc('is_first_admin' as any, { _user_id: userId });
              if (firstAdminError) {
                console.error('is_first_admin RPC error:', firstAdminError);
                setIsFirstAdmin(false);
              } else {
                setIsFirstAdmin(!!firstAdminData);
              }
            } catch (e) {
              console.error('is_first_admin exception:', e);
              setIsFirstAdmin(false);
            }
          } else {
            setIsFirstAdmin(false);
          }
        }

        // Fetch company
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id, name, logo_url, subscription_tier, subscription_status, subscription_expires_at, stripe_customer_id, stripe_subscription_id, max_users, max_locations, max_greenhouses')
          .eq('id', profileData.company_id)
          .single();

        if (companyError) {
          console.error('Company fetch error:', companyError);
        } else {
          setCompany(companyData);
          
          // Whitelist must mirror public.check_user_access — anything outside
          // it (cancelled, expired, incomplete, pending_native_payment, ...)
          // blocks dashboard access. Otherwise a native-free user that signs
          // in via web would bypass payment.
          const ALLOWED_SUB_STATUSES = ['active', 'past_due', 'trial', 'trialing'];
          const statusOk = ALLOWED_SUB_STATUSES.includes(companyData?.subscription_status || '');
          // Cortesía caducada: SOLO status='trialing' sin suscripción Stripe + fecha pasada.
          // Debe reflejar public.check_user_access (acotado a 'trialing' a propósito, para no
          // tocar las 'active' comped con fecha vieja); si no, un reload dejaría entrar a una
          // cortesía ya vencida.
          const courtesyExpired =
            companyData?.subscription_status === 'trialing' &&
            !companyData?.stripe_subscription_id &&
            !!companyData?.subscription_expires_at &&
            new Date(companyData.subscription_expires_at as string) < new Date();
          const isExpired = !statusOk || courtesyExpired;
          setSubscriptionExpired(isExpired);
        }
      } else {
        setRole(null);
        setCompany(null);
        setLinkedCompanies([]);
      }

      setPeritoSinEmpresa(sinRolAqui ? await consultarPeritoSinEmpresa() : false);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      if (reloj) { clearTimeout(reloj); reloj = null; }
      setLoading(false);
    }
  };

  const checkUserAccess = async (userId?: string): Promise<AccessCheckResult> => {
    try {
      const { data, error } = userId
        ? await supabase.rpc('check_user_access', { p_user_id: userId } as any)
        : await supabase.rpc('check_user_access');
      
      if (error) {
        console.error('Access check RPC error:', error);
        // FAIL-CLOSED: If verification fails, deny access
        return { 
          allowed: false, 
          reason: 'ACCESS_VERIFICATION_FAILED' 
        };
      }
      
      // Validate expected response format
      if (data && typeof data === 'object' && 'allowed' in data) {
        return data as unknown as AccessCheckResult;
      }
      
      // Unexpected format = FAIL-CLOSED
      console.error('Access check returned unexpected format:', data);
      return { 
        allowed: false, 
        reason: 'ACCESS_VERIFICATION_FAILED' 
      };
      
    } catch (err) {
      console.error('Access check exception:', err);
      // Exception = FAIL-CLOSED
      return { 
        allowed: false, 
        reason: 'ACCESS_VERIFICATION_FAILED' 
      };
    }
  };

  const signIn = async (emailOrUsername: string, password: string) => {
    const trimmedInput = emailOrUsername.trim();
    let loginEmail = trimmedInput;

    let signedInUserId: string | undefined;

    // If input doesn't contain @, treat as username.
    // M32 (26-09-2026): el usuario y la contraseña van a la edge `login-with-username`,
    // que entra en el servidor y devuelve solo la sesión; el correo de acceso ya no
    // viaja al navegador. Si la edge aún no está desplegada, se entra como antes.
    if (!trimmedInput.includes('@')) {
      const outcome = await loginWithUsername(trimmedInput, password);

      if (outcome.kind === 'error') {
        if (outcome.code === 'ambiguous') throw new Error('USERNAME_AMBIGUOUS');
        if (outcome.code === 'rate_limited') throw new Error('USERNAME_RATE_LIMITED');
        throw new Error('Invalid credentials');
      }

      if (outcome.kind === 'session') {
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession(outcome.session);
        if (sessionError || !sessionData.user) throw sessionError ?? new Error('Invalid credentials');
        signedInUserId = sessionData.user.id;
      } else {
        // Camino de antes (transición): se borra cuando se aplique 20260926140300.
        const { data: resolveResult, error: resolveError } = await supabase
          .rpc('resolve_login_email_by_username', { p_username: trimmedInput });

        if (resolveError) {
          console.error('Username resolve error:', resolveError);
          throw new Error('Invalid credentials');
        }

        const result = resolveResult as { status: string; email?: string };

        if (result.status === 'not_found') {
          throw new Error('Invalid credentials');
        }

        if (result.status === 'ambiguous') {
          throw new Error('USERNAME_AMBIGUOUS');
        }

        if (result.status === 'ok' && result.email) {
          loginEmail = result.email;
        } else {
          throw new Error('Invalid credentials');
        }
      }
    }

    if (!signedInUserId) {
      const { data: signInData, error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password,
      });

      if (error) throw error;
      signedInUserId = signInData.user?.id;
    }

    // Force token flush to WebView storage (Despia iOS/Android race condition fix)
    await supabase.auth.getSession();

    // Pass user_id explicitly to avoid auth.uid() race in mobile WebView
    const accessResult = await checkUserAccess(signedInUserId);
    if (!accessResult.allowed) {
      // Check if the reason is subscription expired - allow login but set flag
      const subscriptionExpiredReasons = [
        'Your company subscription has expired',
        'SUBSCRIPTION_EXPIRED'
      ];
      
      if (subscriptionExpiredReasons.some(r => accessResult.reason?.includes(r) || accessResult.reason === r)) {
        // Allow login but mark subscription as expired
        setSubscriptionExpired(true);
        // Don't show toast - the SubscriptionExpiredPage will explain everything
        return;
      }

      /* P3 (26-09-2026): el perito al que su única empresa desvinculó conserva la
         cuenta, sin empresa. No se le echa: entra y ve «No estás vinculado a ninguna
         empresa», donde canjea un código o un enlace. Cualquier otra cuenta sin
         empresa sigue como antes. */
      if (accessResult.reason === 'No company associated with your account' && (await consultarPeritoSinEmpresa())) {
        setSubscriptionExpired(false);
        setPeritoSinEmpresa(true);
        return;
      }
      
      // For other reasons, logout immediately
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setProfile(null);
      setCompany(null);
      setRole(null);
      setSubscriptionExpired(false);
      
      // Differentiate between verification error and genuine access denial
      if (accessResult.reason === 'ACCESS_VERIFICATION_FAILED') {
        // Technical error - user can retry
        toast.error(i18n.t('toasts.auth.verifyAccessError'));
        throw new Error('ACCESS_VERIFICATION_FAILED');
      } else {
        // Genuinely denied (suspended, blocked, etc.)
        throw new Error(accessResult.reason || 'Access denied');
      }
    }

    setSubscriptionExpired(false);
    toast.success(i18n.t('auth.welcomeBack'));
  };

  const signUp = async (email: string, password: string, fullName: string, companyName?: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          company_name: companyName, // Will be used by trigger if provided
        }
      }
    });

    if (error) throw error;
    
    // The database trigger will automatically create company + profile + role
    toast.success(i18n.t('toasts.auth.accountCreated'));
  };

  const signOut = async () => {
    /* Lo guardado en el teléfono se borra ANTES de nada: aunque la salida falle
       por red, en este aparato no queda nada de esta persona. */
    const saliente = currentUserIdRef.current ?? user?.id ?? null;
    if (saliente) await borrarDatosOffline(saliente, queryClient);
    try {
      // scope: 'local' clears local storage tokens immediately, even if the network call fails.
      // This prevents "phantom session" issues in WebView (Despia native app) where
      // getSession() can return cached tokens for a brief window after signOut.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // Ignore signOut errors - session might already be invalid
      console.warn('Sign out warning:', error);
    }

    // Always clear local state regardless of server response
    setUser(null);
    setSession(null);
    setProfile(null);
    setCompany(null);
    setRole(null);
    setSubscriptionExpired(false);
    setLinkedCompanies([]);
    setPeritoSinEmpresa(false);
    localStorage.removeItem('perito_company_selected');
    // Las ciudades del tiempo van por perfil: al cerrar sesión se tira la lista
    // en memoria. Sin esto, el siguiente que entre en este aparato empezaba con
    // las ciudades del anterior y se le escribían en SU perfil (ver savedCities.ts).
    resetCities();

    // Defensive cleanup of any lingering supabase auth tokens in localStorage
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith('sb-') && k.endsWith('-auth-token'))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore storage access errors
    }

    toast.success(i18n.t('toasts.auth.signedOut'));
    const { isDespiaNative } = await import("@/lib/isDespiaNative");
    navigate(isDespiaNative() ? '/app-auth' : '/auth');
  };

  const refreshProfile = async () => {
    if (!user) return;
    await fetchUserData(user.id);
  };

  const fetchLinkedCompanies = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_perito_linked_companies' as any, { p_user_id: userId });

      if (error) {
        console.error('Linked companies fetch error:', error);
        setLinkedCompanies([]);
        return;
      }

      const rows = (data || []) as any[];
      setLinkedCompanies(
        rows.map((c) => ({
          company_id: c.company_id,
          company_name: c.company_name,
          company_logo: c.company_logo,
          is_usable: c.is_usable ?? true,
        }))
      );
    } catch (err) {
      console.error('fetchLinkedCompanies error:', err);
      setLinkedCompanies([]);
    }
  };

  const switchCompany = async (companyId: string) => {
    if (!user) return;
    const { error } = await supabase.rpc('switch_perito_company', { target_company_id: companyId });
    if (error) throw error;
    /* FUERA LO DE LA EMPRESA ANTERIOR ANTES DE PINTAR LA NUEVA (tanda 5, T01-17).
       Las listas en caché eran de la otra empresa y se seguían viendo hasta que
       cada pantalla recargaba. */
    queryClient.removeQueries();
    // Refetch all user data with new company
    await fetchUserData(user.id);
    await queryClient.invalidateQueries();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        company,
        role,
        loading,
        subscriptionExpired,
        linkedCompanies,
        isFirstAdmin,
        peritoSinEmpresa,
        signIn,
        signUp,
        signOut,
        refreshProfile,
        switchCompany,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
